import { mapGetters } from 'vuex'

import { cloneDeep } from 'lodash'

import { Services } from '@sigma-frontend-libs/commons'

import { CONTACTS_FILTER, generateServices, globalErrorHandler, permissionPresets, request, states, tableHeaderParser } from '@/utils'

import SearchGenerator from '@/components/generator/search'
import { contactFields } from '@/components/services/contacts/utils'

import render from './render'

export default {
  name: 'Template',

  components: {
    'search-generator': SearchGenerator({
      name: Services.contactLists,

      find: {
        defaultFilter: {
          isActive: true,
          type: undefined,
          $scope: [ 'Owner' ]
        },
        alwaysCreateFromWebSocket: true,
        alwaysUpdateFromWebSocket: true,
        alwaysRemoveFromWebSocket: true,

        useCache: false
      },

      serviceForCheckPermissions: Services.contacts,
      filterByIsActive: false
    })
  },

  mixins: [
    generateServices([
      {
        name: Services.contacts,

        outputFilter(result) {
          Object.keys(result).forEach(key => {
            if (result[key] === undefined) {
              result[key] = null
            }
          })

          return result
        },

        find: {
          defaultFilter: Object.assign({}, cloneDeep(CONTACTS_FILTER), {
            $scope: [ 'Owner' ],
            t: undefined
          }),

          alwaysCreateFromWebSocket: true,
          alwaysUpdateFromWebSocket: true,
          alwaysRemoveFromWebSocket: true,

          useCache: false
        },

        get: { useCache: false }
      },
      {
        name: Services.contactLists,

        find: { useCache: false },
        get: { useCache: false },

        remove: {
          redirect: Services.contacts
        }
      }
    ])
  ],

  data() {
    return {
      showDialog: false,
      showFilter: false,
      showFileManager: false,

      exportList: undefined,
      exportType: 'new',

      importContactsResult: {
        state: undefined,
        total: 0
      },

      contactId: undefined,
      contactListId: undefined,
      contactListItem: {},
      contactItem: undefined,
      proxyContactList: undefined,
      canUpdate: false,

      dialogType: undefined,

      show: {
        create: {
          contactLists: false,
          contacts: false
        },
        update: {
          contactLists: false,
          contacts: false
        },
        remove: {
          contactLists: false,
          contacts: false
        }
      }
    }
  },

  computed: {
    ...mapGetters({
      file: 'file/file'
    }),

    canGetContactsAsAdmin() {
      return this.checkPermissions(`advanced.${Services.contacts}.get`)
    },

    canRemoveContacts() {
      return this.checkPermissions(`advanced.${Services.contacts}.remove`, permissionPresets.meUp)
    },

    headers() {
      const result = []
      for (const key of contactFields) {
        const header = {
          title: `${Services.contacts}.labels.${key}`,
          value: key,
          align: 'left',
          class: 'pl-2 pr-2'
        }
        switch (key) {
          case 'custom01':
          case 'custom02':
          case 'custom03': {
            if (this.viewport.size.width <= 1440) {
              continue
            }
          }
          case 'createdAt': {
            header.align = 'right'
            break
          }
          case 'actions': {
            delete header.align
            header.class = 'pl-0 pr-0'
            break
          }
        }
        result.push(header)
      }

      return tableHeaderParser.call(this, result)
    }
  },

  watch: {
    '$route.params.id'() {
      this.restData[Services.contacts].find.data = undefined
      this.restData[Services.contacts].find.pagination.total = 0
      this.restData[Services.contacts].find.state = states.empty

      this.getCurrentContactList()
    },

    file: {
      handler(file) {
        this.showImportContacts = true
        this.showFileManager = false
        this.importContacts(file.key)
      },
      deep: true
    }
  },

  mounted() {
    this.handleCreateMode()
    this.getCurrentContactList()
  },

  beforeDestroy() {
    this.restData[Services.contacts].find.filter = cloneDeep(CONTACTS_FILTER)
  },

  methods: {
    handleCreateMode() {
      if (this.$route.query?.mode === 'create') {
        this.dialogType = `${Services.contactLists}.create`
        this.showDialog = true

        this.$router.replace({
          query: {
            ...this.$route.query,
            mode: undefined
          }
        })
      }
    },

    async getCurrentContactList() {
      const id = this.$route.params.id
      if (id) {
        this.proxyContactList = cloneDeep(await this.rest.contactLists.get(id))
        this.restData[Services.contacts].find.filter.ListId = id
        this.restData[Services.contacts].find.filter.t = Date.now()
      }
    },

    async importContacts(key) {
      this.importContactsResult.state = states.loading
      try {
        const result = await request.update(Services.contacts, {
          body: {
            ListId: this.$route.params.id,
            from: { key }
          }
        })
        if (result) {
          this.rest[Services.contacts].find()
          const total = result.total
          if (total) {
            this.importContactsResult.state = states.success
            this.importContactsResult.total = result.total
          } else {
            this.importContactsResult.state = states.error
          }
        }
      } catch (error) {
        this.importContactsResult.state = states.error

        globalErrorHandler(error)
      }
    },

    async exportContacts(id) {
      try {
        await request.update(Services.contactLists, {
          body: {
            ListId: id,
            from: { ListId: this.$route.params.id }
          }
        })
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.showExportContacts = false
      }
    },

    async tryUpdating() {
      if (this.restData[Services.contacts].update.isValid) {
        try {
          await this.rest.contacts.update(this.contactItem.id, this.contactItem)
          this.contactItem = undefined
          this.show.update.contacts = false
        } catch (error) {
          globalErrorHandler(error)
        }
      }
    },

    async changeData(service, method, data) {
      let valid = this.restData[service][method].isValid
      if (method === 'remove') {
        valid = this.canRemoveContacts
      }

      if (method && service && data) {
        if (this.restData[service][method].state !== states.loading && valid) {
          try {
            const result = await this.rest[service][method](data)
            if (result) {
              this.show[method][service] = false
            }

            return result
          } catch (error) {
            globalErrorHandler(error)
          }
        }
      }
    }
  },

  render
}
